import React from 'react';
import { motion } from 'framer-motion';
import isEmpty from 'lodash/isEmpty';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import TableBody from './TableBody';

type ChildTablePositions = 'start' | 'middle' | 'end';

interface ChildTableProps {
  position?: ChildTablePositions;
  isVisible?: boolean;
}

const ChildTableContainer = styled(motion.div)<ChildTableProps>`
  width: 100%;
  height: 0;
  overflow: hidden;

  ${({ position }) =>
    position === 'end' &&
    css`
      padding-right: ${toRem(theme.space.xl)};
    `}
`;

const ChildTable = styled.table<ChildTableProps>`
  position: relative;
  width: 100%;
  padding: 0;
  margin-bottom: ${toRem(theme.space.md)};
  border-collapse: separate;
  border-spacing: 0;
  border: none;
  overflow: hidden;

  ${({ position }) =>
    position === 'start' &&
    css`
      padding-left: ${toRem(theme.space.md)};
    `}
`;

export const ChildRow = styled.tr<ChildTableProps>`
  width: 100%;
  padding: 0;
  text-align: inherit;

  &:last-child {
    margin-bottom: 0;
  }

  ${({ position }) =>
    position === 'start' &&
    css`
      td {
        padding-left: ${toRem(theme.space.md)};
      }

      &:first-child {
        td {
          border-top-left-radius: ${toRem(5.5)};
        }
      }

      &:last-child {
        td {
          border-bottom-left-radius: ${toRem(5.5)};
        }
      }
    `}

  ${({ position }) =>
    position === 'end' &&
    css`
      &:first-child {
        td {
          border-top-right-radius: ${toRem(5.5)};
        }
      }

      &:last-child {
        td {
          border-bottom-right-radius: ${toRem(5.5)};
        }
      }
    `}
`;

export const ChildCell = styled.td`
  && {
    position: relative;
    z-index: auto;
    width: 100%;
    margin-bottom: 0;
    padding: ${toRem(theme.space.md)} 0;
    background-color: ${theme.color.neutral[50]};
    text-align: inherit;

    &:first-child {
      position: relative;
      z-index: auto;
      min-width: auto;
      background-color: ${theme.color.neutral[50]};

      &::after {
        display: none;
      }
    }
  }
`;

const ChildCellInner = styled.div`
  padding: 0 ${toRem(theme.space.xl)};
  text-align: inherit;
`;

interface ChildDataProps extends ChildTableProps {
  data?: string[];
}

export const ChildData: React.FC<ChildDataProps> = ({ position, isVisible, data }) =>
  data && !isEmpty(data) ? (
    <ChildTableContainer position={position} animate={{ height: isVisible ? 'auto' : 0 }} transition={theme.transition.spring.accordion}>
      <ChildTable position={position}>
        <TableBody>
          {data.map((datum, index) => (
            <ChildRow key={`${index}-${datum}`} position={position}>
              <ChildCell>
                <ChildCellInner>{datum}</ChildCellInner>
              </ChildCell>
            </ChildRow>
          ))}
        </TableBody>
      </ChildTable>
    </ChildTableContainer>
  ) : null;
